import { useState, type FC, type SyntheticEvent } from 'react';
import { Tab, TabList, Tabs } from '@selfinspectionorg/ui-kit';
import styles from './ProfileView.module.scss';
import { Card } from '@/components/ui';
import { SectionHeading } from '@/modules/profile/components';
import { useUserProfile } from '@/shared/api/user';
import { navigateBack } from '@/shared/auth/backNavigation';
import { GeneralTab } from '@/modules/profile/features/general';
import { ChangePasswordForm } from '@/modules/profile/features/password';
import { MfaSections } from '@/modules/profile/features/mfa';
import { ApiTab } from '@/modules/profile/features/api-token';

type ProfileTab = 'general' | 'security' | 'api';

interface Props {
  /** Where Back returns to; falls back to the user's portal when absent. */
  returnUrl: string | null;
}

/**
 * The Profile page: a titled card with a Back control and General / Security / API tabs. The profile
 * query is owned here and shared with the General and API tabs. Tab panels stay mounted (hidden when
 * inactive) so switching tabs never reloads the page or loses unsaved form state.
 */
export const ProfileView: FC<Props> = ({ returnUrl }) => {
  const [tab, setTab] = useState<ProfileTab>('general');
  const { profile, isLoading, error, mutate } = useUserProfile();

  const goBack = () => navigateBack(returnUrl);
  const onTabChange = (_: SyntheticEvent, value: string | number) => setTab(value as ProfileTab);

  return (
    <Card className={styles.page}>
      <header className={styles.header}>
        <Card.BackButton onClick={goBack} testId="profile-back-button" />
        <h1 className={styles.title}>Profile</h1>
      </header>

      <Tabs value={tab} onChange={onTabChange}>
        <TabList aria-label="Profile settings">
          <Tab value="general" label="General" className={styles.tab} />
          <Tab value="security" label="Security" className={styles.tab} />
          <Tab value="api" label="API" className={styles.tab} />
        </TabList>
      </Tabs>

      {/* Panels stay mounted; `hidden` keeps the inactive ones in the DOM so their state survives a
          tab switch (per the spec: switching tabs must not lose unsaved state). */}
      <div className={styles.panels}>
        <div role="tabpanel" hidden={tab !== 'general'} data-testid="general-panel">
          <GeneralTab profile={profile} isLoading={isLoading} error={error} mutate={mutate} />
        </div>

        <div role="tabpanel" hidden={tab !== 'security'} data-testid="security-panel">
          <div className={styles.securityTab}>
            <section className={styles.section}>
              <SectionHeading title="Password" />
              <ChangePasswordForm />
            </section>
            <hr className={styles.divider} />
            <MfaSections />
          </div>
        </div>

        <div role="tabpanel" hidden={tab !== 'api'} data-testid="api-panel">
          <ApiTab token={profile?.apiKey ?? ''} />
        </div>
      </div>
    </Card>
  );
};
